import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Spinner, Button, Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { useTranslation } from "react-i18next";
import {
    addProvinceAction,
    updateDictionaireAction,
    deleteDictionaireAction,
    loadingToggleAction,
    downloadProvinceTemplateAction
} from '../../../../store/actions/AdministrationAction';
import { 
    importProvincesFileUploadAction,
    startLoader
} from '../../../../store/actions/ProviderActions';

const ProvinceTab = ({ provinces, showLoading, showProvLoading, hasMenu, canDo, dispatch }) => {
    const { t } = useTranslation();
    let errorsObj = { name: '', code: '' };
    
    const [nameFr, setNameFr] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [code, setCode] = useState('');
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState(errorsObj);
    const [updateMode, setUpdateMode] = useState(false);
    const [dataUpdateId, setDataUpdateId] = useState(null);
    const [addModal, setAddModal] = useState(false);

    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    };

    const renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <PButton 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={clearFilter}
                    className="me-2"
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={(e) => onGlobalFilterChange(e)} 
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    };

    const clearFilter = () => {
        initFilters();
    };

    const setColumns = () => {
        return [
            {name: 'nameFr', headerName: t('common.name'), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'createdAt', filterField: 'createdAt', dataType: 'date', headerName: t('common.created-on'), filterPlaceholder: t('common.filter-by-creation'), style: { minWidth: '6rem' }, filter: true, bodyTemplate: datePaiementBodyTemplate, filterElement: dateFilterTemplate},
            {name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '6rem'}, filter: false, bodyTemplate: getActionsTemplate},
        ]
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const getActionsTemplate = (rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAM') && canDo('MENPARAM', 'UPDATEPARAM') && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => updateData(rowData)}
                        title={t('common.edit')}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAM') && canDo('MENPARAM', 'DELETEPARAM') && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title={t('common.delete')}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    };

    const handleDelete = (rowData) => {
        swal({
            title: t('common.delete-the') + "région " + rowData.nameFr + " ?",
            text: t('common.once') + "région" + t('common.content-deleted'),
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                dispatch(deleteDictionaireAction(rowData.id, 'province'));
            }
        });
    };

    const updateData = (data) => {
        setNameFr(data.nameFr || '');
        setNameEn(data.nameEn || '');
        setCode(data.code || '');
        setUpdateMode(true);
        setDataUpdateId(data.id);
        setAddModal(true);
    };

    const dateFilterTemplate = (options) => {
        return <Calendar 
            value={options.value} 
            onChange={(e) => options.filterCallback(e.value, options.index)} 
            dateFormat="dd/mm/yy" 
            placeholder="dd/mm/yyyy" 
            mask="99/99/9999" 
        />;
    };

    const datePaiementBodyTemplate = (rowData) => {
        return formatDate(rowData.createdAt);
    };

    const formatDate = (value) => {
        return value.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const resetForm = () => {
        setNameFr('');
        setNameEn('');
        setCode('');
        setFile(null);
        setErrors(errorsObj);
        setUpdateMode(false);
        setDataUpdateId(null);
    };

    const handleDownloadTemplate = () => {
        dispatch(downloadProvinceTemplateAction());
    };

    useEffect(() => {
        initFilters();
    }, []);

    function onProvinceSave(e) {
        e.preventDefault();
        let error = false;
        const errorObj = { ...errorsObj };
        
        if (file) {
            dispatch(startLoader());
            dispatch(importProvincesFileUploadAction(file));
            return;
        }
        
        if (nameFr === '') {
            errorObj.name = t('common.name-required');
            error = true;
        }
        
        setErrors(errorObj);
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }
        
        dispatch(loadingToggleAction(true));

        if (updateMode) {
            let obj = { nameFr: nameFr, nameEn: nameFr, reference: 'province', code };
            dispatch(updateDictionaireAction(dataUpdateId, obj, 'province'));
        } else {
            let obj = { nameFr: nameFr, nameEn: nameFr, reference: 'province', code };
            dispatch(addProvinceAction(obj));
        }
        
        setAddModal(false);
        resetForm();
    }

    function selectFile(event) {
        setFile(event.target.files[0]);
    }

    return (
        <>
            {/* Header */}
            <div className="provider-type-header">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <h4 className="mb-1 text-white">{t('common.region')}</h4>
                        <p className="mb-0 small text-white-50">{t('common.manage-regions')}</p>
                    </div>
                    <div className="d-flex gap-3">
                        <button
                            type="button"
                            className="btn btn-outline-light btn-sm"
                            onClick={handleDownloadTemplate}
                        >
                            <i className="fa fa-download me-2"></i> 
                            {t("common.download-template")}
                        </button>
                        
                        {/* {hasMenu('MENPARAM') && canDo('MENPARAM', 'CREATEPARAM') && ( */}
                            <button
                                type="button"
                                className="btn btn-outline-light btn-sm"
                                onClick={() => {
                                    resetForm();
                                    setAddModal(true);
                                }}
                            >
                                <i className="fa fa-plus-circle me-2"></i> 
                                {t("common.new-region")}
                            </button>
                        {/* )} */}
                    </div>
                </div>
            </div>

            {/* Modal for creating/editing province */}
            <Modal 
                show={addModal} 
                onHide={() => setAddModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {updateMode ? t("common.update-region") + nameFr : t("common.new-region")}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form>
                        <div className="mb-3">
                            <label className="form-label">{t('common.code')} <small className="text-muted">({t('common.optional')})</small></label>
                            <input
                                type="text"
                                className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                placeholder="CE"
                                value={code}
                                disabled={showLoading || showProvLoading || file}
                                onChange={(e) => setCode(e.target.value)}
                            />
                            {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">{t('common.name')}</label>
                            <input
                                type="text"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Centre"
                                value={nameFr}
                                disabled={showLoading || showProvLoading || file}
                                onChange={(e) => setNameFr(e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                        
                        <hr className="my-4" />
                        
                        <div className="mb-3">
                            <h6 className="text-primary mb-3">{t("common.import-csv-activity")}</h6>
                            <div className="input-group">
                                <span className="input-group-text">CSV</span>
                                <input 
                                    type="file" 
                                    className="form-control" 
                                    onChange={selectFile}
                                    accept=".csv"
                                />
                            </div>
                            {file && (
                                <div className="alert alert-info mt-2 py-2">
                                    <small>File selected: <strong>{file.name}</strong></small>
                                </div>
                            )}
                        </div>
                    </form>
                </Modal.Body>
                
                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => setAddModal(false)}
                        disabled={showLoading || showProvLoading}
                    >
                        {t('common.cancel')}
                    </button>
                    
                    {(showLoading || showProvLoading) ? (
                        <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t('common.saving')}</span>
                        </Button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={onProvinceSave}
                        >
                            {t('common.save')}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* DataTable */}
            <div className="card border-0 shadow-sm bg-white">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={provinces ? provinces.map(elt => {
                            elt.createdAt = new Date(elt.createdAt ? elt.createdAt : elt.created);
                            return elt;
                        }) : []}
                        emptyMessage="Aucune province trouvée"
                        filters={filters}
                        globalFiltersFields={['nameFr', 'createdAt']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: t('common.name'), dataKey: 'nameFr' },
                            { title: t('common.create-date'), dataKey: 'createdAt' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename="provinces"
                        loading={showLoading}
                        lazy={false}
                    />
                </div>
            </div>
        </>
    );
};

export default ProvinceTab;